import { useCallback, useState } from 'react';

import { useHeroToast } from '../../../hooks';
import { useImportStockPhoto } from '../../../queries';
import { useAdsGeneratorStore } from '../model/store';
import { AdScene } from '../model/types';
import { collectWatermarkedPreviews, WatermarkedPreview } from '../utils/scene';

interface LicensePreviewsApi {
  /** Podglądy ze znakiem wodnym leżące na planszy. */
  previews: WatermarkedPreview[];
  /** Pobiera licencjonowane pliki i podmienia je na planszy. */
  licenseAll: () => Promise<boolean>;
  isLicensing: boolean;
  /** Który podgląd jest właśnie pobierany, do pokazania postępu. */
  progress: number;
}

/**
 * Zamiana roboczych podglądów ze stocka na licencjonowane pliki.
 *
 * Podgląd na planszy jest znakowany i wczytany bez zgody CORS, więc kreacji
 * z nim nie da się wyeksportować. Zamiast blokować pracę, dociągamy właściwe
 * pliki i podmieniamy adresy w miejscu — układ, kadr i filtry zostają.
 *
 * Pobrania idą PO KOLEI: każde zabiera sztukę z dziennej puli, więc licznik ma
 * rosnąć przewidywalnie, a ewentualna odmowa dotyczyć jednej pozycji.
 */
export const useLicensePreviews = (scene: AdScene | null): LicensePreviewsApi => {
  const updateElement = useAdsGeneratorStore((state) => state.updateElement);
  const setBackground = useAdsGeneratorStore((state) => state.setBackground);
  const commit = useAdsGeneratorStore((state) => state.commit);
  const { mutateAsync: importPhoto } = useImportStockPhoto();
  const { showError } = useHeroToast();

  const [isLicensing, setIsLicensing] = useState(false);
  const [progress, setProgress] = useState(0);

  const previews = scene ? collectWatermarkedPreviews(scene) : [];

  const licenseAll = useCallback(async (): Promise<boolean> => {
    if (!scene) return false;

    const pending = collectWatermarkedPreviews(scene);
    if (pending.length === 0) return true;

    const missingId = pending.filter((item) => !item.stockId);
    if (missingId.length > 0) {
      showError(
        'Któryś podgląd nie pamięta, z którego zdjęcia pochodzi. Usuń go z planszy i wstaw zdjęcie ponownie z banku.',
      );
      return false;
    }

    setIsLicensing(true);
    setProgress(0);
    commit();

    try {
      for (const item of pending) {
        // eslint-disable-next-line no-await-in-loop
        const result = await importPhoto({
          id: item.stockId as number,
          usage: item.elementId === null ? 'background' : 'element',
          canvasLongEdge: Math.max(scene.width, scene.height),
        });

        if (item.elementId === null) {
          setBackground({ imageSrc: result.asset.url, imageStockId: undefined }, true);
        } else {
          updateElement(item.elementId, { src: result.asset.url, stockId: undefined }, true);
        }

        setProgress((current) => current + 1);
      }

      return true;
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Nie udało się pobrać licencjonowanych zdjęć');
      return false;
    } finally {
      setIsLicensing(false);
    }
  }, [commit, importPhoto, scene, setBackground, showError, updateElement]);

  return { previews, licenseAll, isLicensing, progress };
};
